import { Icons } from "@/components/icons";
import { FaLinkedin, FaTwitter } from "react-icons/fa";
import { FaYoutube } from "react-icons/fa6";
import { RiInstagramFill } from "react-icons/ri";

export const BLUR_FADE_DELAY = 0.15;

const germanUrls: Record<string, string> = {
  '/contact': '/kontakt',
  '/solutions/ai-consulting': '/solutions/ki-beratung',
  '/solutions/automatisation': '/solutions/automatisierung',
  '/solutions/industry-4-0': '/solutions/industrie-4-0',
  '/privacy': '/datenschutz',
  '/legal': '/impressum'
};

export const siteConfig = {
  getLocalizedUrl: (path: string, locale: string = '') => {
    // Entferne alle Locale-Präfixe
    const cleanPath = path.replace(/^\/(de|en|zh-CN)/, '');

    // Wenn deutsch, prüfe ob wir eine übersetzte URL haben
    if (locale === 'de' && germanUrls[cleanPath]) {
      return `/${locale}${germanUrls[cleanPath]}`;
    }

    // Ansonsten füge einfach das Locale-Präfix hinzu
    return `/${locale}${cleanPath}`;
  },
  name: "SavagesCorp",
  description: "Automate your workflow with AI",
  url: process.env.NEXT_PUBLIC_APP_URL || "http://localhost:3000",
  keywords: ["SaaS", "Template", "Next.js", "React", "Tailwind CSS"],
  links: {
    email: "contact@savagescorp.com",
  },
  header: (locale: string = '') => {
    const t = (key: { de: string; en: string; 'zh-CN': string }) => {
      if (locale === 'de') return key.de;
      if (locale === 'zh-CN') return key['zh-CN'];
      return key.en;
    };

    return [
      {
        trigger: t({ de: "Lösungen", en: "Solutions", 'zh-CN': "解决方案" }),
        content: {
          items: [
            {
              title: t({ de: "KI-Beratung", en: "AI Consulting", 'zh-CN': "AI 咨询" }),
              href: siteConfig.getLocalizedUrl("/solutions/ai-consulting", locale),
              description: t({
                de: "Strategische KI-Beratung für Unternehmen jeder Größe.",
                en: "Strategic AI consulting for businesses of all sizes.",
                'zh-CN': "为各种规模的企业提供战略性AI咨询服务。"
              }),
            },
            {
              title: t({ de: "Automatisierung", en: "Automation", 'zh-CN': "自动化" }),
              href: siteConfig.getLocalizedUrl("/solutions/automatisation", locale),
              description: t({
                de: "Intelligente Automatisierungslösungen für mehr Effizienz.",
                en: "Intelligent automation solutions for enhanced efficiency.",
                'zh-CN': "智能自动化解决方案，提升效率。"
              }),
            },
            {
              title: t({ de: "Industrie 4.0", en: "Industry 4.0", 'zh-CN': "工业 4.0" }),
              href: siteConfig.getLocalizedUrl("/solutions/industry-4-0", locale),
              description: t({
                de: "Smart Manufacturing und digitale Fabrik-Lösungen.",
                en: "Smart manufacturing and digital factory solutions.",
                'zh-CN': "智能制造和数字化工厂解决方案。"
              }),
            },
          ],
        },
      },
      {
        href: siteConfig.getLocalizedUrl("/about", locale),
        label: t({ de: "Über uns", en: "About Us", 'zh-CN': "关于我们" }),
      },
      ...(locale !== 'zh-CN' ? [{
        href: siteConfig.getLocalizedUrl("/blog", locale),
        label: "Blog",
      }] : []),
      {
        href: siteConfig.getLocalizedUrl("/contact", locale),
        label: t({ de: "Kontakt", en: "Contact", 'zh-CN': "联系我们" }),
      },
    ];
  },
  pricing: [
    {
      name: "BASIC",
      href: "#",
      price: "$19",
      period: "month",
      yearlyPrice: "$16",
      features: [
        "1 User",
        "5GB Storage",
        "Basic Support",
        "Limited API Access",
        "Standard Analytics",
      ],
      description: "Perfect for individuals and small projects",
      buttonText: "Subscribe",
      isPopular: false,
    },
    {
      name: "PRO",
      href: "#",
      price: "$49",
      period: "month",
      yearlyPrice: "$40",
      features: [
        "5 Users",
        "50GB Storage",
        "Priority Support",
        "Full API Access",
        "Advanced Analytics",
      ],
      description: "Ideal for growing businesses and teams",
      buttonText: "Subscribe",
      isPopular: true,
    },
    {
      name: "ENTERPRISE",
      href: "#",
      price: "$99",
      period: "month",
      yearlyPrice: "$82",
      features: [
        "Unlimited Users",
        "500GB Storage",
        "24/7 Premium Support",
        "Custom Integrations",
        "AI-Powered Insights",
      ],
      description: "For large-scale operations and high-volume users",
      buttonText: "Subscribe",
      isPopular: false,
    },
  ],
  faqs: [
    {
      question: "What makes SavagesCorp unique?",
      answer: (
        <span>
          We combine deep technical expertise with a human-centric approach. Our solutions are not just technologically advanced, but also practical and tailored to real business needs. We focus on delivering measurable results and long-term value.
        </span>
      ),
    },
    {
      question: "How do you work with clients?",
      answer: (
        <span>
          We follow a collaborative approach: First, we understand your challenges through in-depth discovery sessions. Then, we develop a tailored strategy and implementation plan. Throughout the project, we maintain transparent communication and provide regular progress updates.
        </span>
      ),
    },
    {
      question: "Which industries do you serve?",
      answer: (
        <span>
          We work across multiple sectors including Financial Services, Healthcare, Manufacturing, Retail & E-commerce, and Supply Chain. Each industry team combines domain expertise with technical knowledge to deliver optimal solutions.
        </span>
      ),
    },
    {
      question: "What's your typical project process?",
      answer: (
        <span>
          Our process includes: Initial consultation and needs assessment, strategy development, solution design, implementation, testing, and deployment. We also provide post-launch support and continuous optimization to ensure long-term success.
        </span>
      ),
    },
    {
      question: "What are your quality standards?",
      answer: (
        <span>
          We maintain rigorous quality standards through: Comprehensive testing protocols, regular code reviews, adherence to industry best practices, and continuous monitoring. All our solutions comply with relevant regulations and security standards.
        </span>
      ),
    },
  ],
  footer: (locale: string = '') => {
    const t = (key: { de: string; en: string; 'zh-CN': string }) => {
      if (locale === 'de') return key.de;
      if (locale === 'zh-CN') return key['zh-CN'];
      return key.en;
    };

    return [
      {
        title: t({ de: "Lösungen", en: "Solutions", 'zh-CN': "解决方案" }),
        links: [
          {
            href: siteConfig.getLocalizedUrl("/solutions/ai-consulting", locale),
            text: t({ de: "KI-Beratung", en: "AI-Consulting", 'zh-CN': "AI 咨询" }),
            icon: null
          },
          {
            href: siteConfig.getLocalizedUrl("/solutions/automatisation", locale),
            text: t({ de: "Automatisierung", en: "Automation", 'zh-CN': "自动化" }),
            icon: null
          },
          {
            href: siteConfig.getLocalizedUrl("/solutions/industry-4-0", locale),
            text: t({ de: "Industrie 4.0", en: "Industry 4.0", 'zh-CN': "工业 4.0" }),
            icon: null
          },
        ],
      },
      {
        title: t({ de: "Unternehmen", en: "Company", 'zh-CN': "公司" }),
        links: [
          {
            href: siteConfig.getLocalizedUrl("/about", locale),
            text: t({ de: "Über uns", en: "About Us", 'zh-CN': "关于我们" }),
            icon: null
          },
          // Hide blog link for Chinese locale
          ...(locale !== 'zh-CN' ? [{
            href: siteConfig.getLocalizedUrl("/blog", locale),
            text: "Blog",
            icon: null
          }] : []),
        ],
      },
      {
        title: t({ de: "Ressourcen", en: "Resources", 'zh-CN': "资源" }),
        links: [
          {
            href: siteConfig.getLocalizedUrl("/contact", locale),
            text: t({ de: "Kontakt", en: "Contact", 'zh-CN': "联系我们" }),
            icon: null
          },
        ],
      },
      {
        title: "Social",
        links: [
          {
            href: "https://hk.linkedin.com/company/savagescorp",
            text: "LinkedIn",
            icon: <FaLinkedin />,
            isTargetBlank: true
          },
        ],
      },
    ];
  },
};

export type SiteConfig = typeof siteConfig;
